import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, Phone, Mail, Home, ChefHat, Laptop, Map } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Raum-Informationen Sektion
 * Zeigt Bilder, Features und Kontaktdaten des Veranstaltungsraums
 * Verwendet Animationen für eine ansprechende Darstellung
 */
export default function RoomInfo() {
  /**
   * Bildergalerie des Veranstaltungsraums
   * Zeigt verschiedene Ansichten und Bereiche des Raums
   */
  const images = [
    { src: "/images/Arenarisquelle-5.avif", alt: "Veranstaltungsraum Arenarisquelle - Blick in den Raum mit Tischen und Stühlen" },
    { src: "/images/photo2.avif", alt: "Veranstaltungsraum Arenarisquelle - Festlich geschmückte Tafel während einer Veranstaltung" },
    { src: "/images/Arenarisquelle-3.avif", alt: "Veranstaltungsraum Arenarisquelle - Küche mit Geräten" },
    { src: "/images/Arenarisquelle-1.avif", alt: "Veranstaltungsraum Arenarisquelle - Raum mit Tischen und Stühlen und Blick in die Küche" }
  ];

  /**
   * Kontaktdaten für Anfragen
   * Enthält alle wichtigen Kontaktinformationen
   */
  const contact = [
    { icon: Phone, label: "Telefon", value: "+49 36259 5600" },
    { icon: Mail, label: "E-Mail", value: "info@bad-tabarz.de" },
    { icon: MapPin, label: "Adresse", value: "Lauchagrundstraße 72, 99891 Bad Tabarz" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white" aria-labelledby="room-info-heading">
      <div className="max-w-7xl mx-auto px-4">
        {/* Überschrift und Beschreibung */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 id="room-info-heading" className="text-4xl md:text-5xl font-light mb-6 text-[#0D5C63]">
            Ihr <span className="font-semibold">Veranstaltungsort</span> in Bad Tabarz
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Idyllisch und mitten im schönen Thüringer Wald gelegen, bietet der Veranstaltungsraum Arenarisquelle den perfekten Rahmen für Ihre Feierlichkeiten und besonderen Momente in Bad Tabarz.
          </p>
        </motion.div>

        {/* Bildergalerie mit Hover-Effekten */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, staggerChildren: 0.1 }}
          viewport={{ once: true }}
        >
          {images.map((image, index) => (
            <motion.div 
              key={index}
              className="aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
              whileHover={{ y: -5 }}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Features und Kontakt in zwei Spalten */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Features-Bereich */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-semibold mb-8 text-[#0D5C63]">
              Ausstattung & Service
            </h3>
            <Accordion type="single" collapsible className="w-full space-y-6">
              <AccordionItem value="raum" className="border-none">
                <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-0">
                    <AccordionTrigger className="hover:no-underline px-6 py-6 [&>svg]:text-[#0D5C63] [&>svg]:w-5 [&>svg]:h-5">
                      <div className="flex items-start gap-4 w-full">
                        <div className="p-3 rounded-xl bg-[#A8D08D]">
                          <Home className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-lg font-semibold mb-2 text-[#0D5C63]">Platz für bis zu 50 Personen</h4>
                          <p className="text-gray-600">Flexible Nutzungsmöglichkeiten</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-4 text-gray-700 leading-relaxed pl-16">
                        <p>
                          Die Location befindet sich in der oberen der beiden Etagen des Gebäudes in der Lauchagrundstraße 72 in Bad Tabarz und ist ausschließlich über eine Treppe erreichbar. Wir weisen darauf hin, dass kein barrierefreier Zugang vorhanden ist.
                        </p>
                        <p>
                          Der Raum ist mit Tischen und Stühlen ausgestattet. Optisch bietet er ein neutrales Design: Die hohen weißen Wände lassen viel Platz für persönliche Gestaltungsmöglichkeiten, um ihn nach eigenen Vorlieben zu dekorieren und zu schmücken. An allen Wänden sind dafür Vorhangdrahte angebracht, die es ermöglichen, Girlanden, Bilder oder andere dekorative Elemente anzubringen. Der glatte Parkettboden sorgt für eine angenehme Atmosphäre.
                        </p>
                        <p>
                          Die Location verfügt zudem über eine Damen- sowie eine Herrentoilette.
                        </p>
                      </div>
                    </AccordionContent>
                  </CardContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="kueche" className="border-none">
                <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-0">
                    <AccordionTrigger className="hover:no-underline px-6 py-6 [&>svg]:text-[#0D5C63] [&>svg]:w-5 [&>svg]:h-5">
                      <div className="flex items-start gap-4 w-full">
                        <div className="p-3 rounded-xl bg-[#A8D08D]">
                          <ChefHat className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-lg font-semibold mb-2 text-[#0D5C63]">Küche & Geschirr</h4>
                          <p className="text-gray-600">Großzügige Küche mit vollständiger Ausstattung</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-4 text-gray-700 leading-relaxed pl-16">
                        <p>
                          Ein Highlight ist die großzügige, separate Küche. Hier ist genug Platz für ein aufgebautes Buffet; die lange Küchenzeile bietet viel Arbeitsfläche und Stauraum. In der Mitte der Küche befindet sich eine Kochinsel, ausgestattet mit zwei Backöfen sowie zwei Elektro-Herden, die mit je vier Herdplatten versehen sind. Ein großer Einbaukühlschrank mit Gefrierfächern ist ebenfalls vorhanden.
                        </p>
                        <p>
                          <strong>Die Nutzung des Kücheninventars, wie Geschirr, Gläser und Besteck ist im Preis enthalten.</strong>
                        </p>
                      </div>
                    </AccordionContent>
                  </CardContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="technik" className="border-none">
                <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-0">
                    <AccordionTrigger className="hover:no-underline px-6 py-6 [&>svg]:text-[#0D5C63] [&>svg]:w-5 [&>svg]:h-5">
                      <div className="flex items-start gap-4 w-full">
                        <div className="p-3 rounded-xl bg-[#A8D08D]">
                          <Laptop className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-lg font-semibold mb-2 text-[#0D5C63]">WLAN verfügbar</h4>
                          <p className="text-gray-600">Hinweise zur Technikausstattung</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="text-gray-700 leading-relaxed pl-16">
                        <p>
                          Eventuelle Technik, wie Laptop, Beamer oder Musikanlagen sind nicht vorhanden und müssen selbst organisiert werden. WLAN kann auf Anfrage während der Veranstaltung genutzt werden.
                        </p>
                      </div>
                    </AccordionContent>
                  </CardContent>
                </Card>
              </AccordionItem>
            </Accordion>
          </motion.div>

          {/* Kontakt-Bereich */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            
            {/* Kontakt */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold mb-8 text-[#0D5C63]">
                Kontakt & Anfahrt
              </h3>
              
              <Card className="border-none shadow-md">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {contact.map((item, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-[#C9A227]">
                          {React.createElement(item.icon, { className: "w-5 h-5 text-white" })}
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 font-medium">{item.label}</p>
                          <p className="text-lg font-semibold">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="attraktionen" className="border-none">
                  <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-0">
                      <AccordionTrigger className="hover:no-underline px-6 py-6 [&>svg]:text-[#0D5C63] [&>svg]:w-5 [&>svg]:h-5">
                        <div className="flex items-start gap-4 w-full">
                          <div className="p-3 rounded-xl bg-[#A8D08D]">
                            <Map className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 text-left">
                            <h4 className="text-lg font-semibold mb-2 text-[#0D5C63]">Attraktionen in der Nähe</h4>
                            <p className="text-gray-600">Freizeitmöglichkeiten für die ganze Familie</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="space-y-4 text-gray-700 leading-relaxed pl-16">
                          <p>
                            Unweit von des Veranstaltungsraums befinden sich zahlreiche kleine Attraktionen für die ganze Familie: Gleich nebenan lädt die Kuranlage Arenarisquelle mit Kneipp-Wassertretbecken, Bänken, Liegestühlen, Steinpark, Dinosaurierskulpturen und Barfußpfad zum Kneippen und Entspannen ein. Oder Sie schlendern gemütlich über die gegenüberliegende Märchenwiese. Auf dem Pumptrack (kleiner MTB-Rundkurs) und Trimm-Dich-Pfad kommen die Sport- und Bikefans auf ihre Kosten. Und auch an die Kleinsten ist gedacht: Direkt vor den Türen der Location befindet sich ein Spielplatz mit Matschküche, Schaukel und Rutsche.
                          </p>
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className="font-semibold mb-3 text-[#0D5C63]">Weitere Highlights:</h4>
                            <ul className="space-y-2 list-none">
                              <li className="flex items-start gap-2">
                                <span className="text-[#A8D08D] font-bold mt-1">·</span>
                                <span>Kuranlage Arenarisquelle mit Kneipp-Wassertretbecken, Bänken, Liegestühlen, Steinpark, Dinosaurierskulpturen und Barfußpfad</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-[#A8D08D] font-bold mt-1">·</span>
                                <span>Märchenwiese</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-[#A8D08D] font-bold mt-1">·</span>
                                <span>Pumptrack (kleiner MTB-Rundkurs) und Trimm-Dich-Pfad</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-[#A8D08D] font-bold mt-1">·</span>
                                <span>Spielplatz mit Matschküche, Schaukel und Rutsche</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </AccordionContent>
                    </CardContent>
                  </Card>
                </AccordionItem>
              </Accordion>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}